import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, BellRing, ClipboardCheck, UserPlus, PauseCircle, Clock, Layers, Siren, ChevronRight, Sparkles, Lock, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Hero } from "@/pages/Home";
import { NativeSelect, Semaphore, StatusBadge, OverdueBadge, DemoBadge } from "@/components/common";
import { fmtDT, fmtWhen } from "@/lib/format";
import { cn } from "@/lib/utils";

const ICONS = { overdue: AlertCircle, no_motivation: BellRing, escalated: Siren, to_verify: ClipboardCheck, unassigned: UserPlus, suspended: PauseCircle, due_soon: Clock, open: Layers, cr_urgent: Siren, cr_open: AlertCircle, cr_verify: ClipboardCheck, insp_overdue: Clock, assets_broken: Siren, assets_maint: Clock, assets_nocheck: ClipboardCheck, assets_possession: UserPlus };
const RESP_HIDDEN = ["escalated", "unassigned", "due_soon", "no_motivation", "cr_open", "insp_overdue", "assets_possession", "assets_maint"];
const PERIODS = { "": "Qualsiasi scadenza", "7": "Scadenza entro 7 giorni", "30": "Scadenza entro 30 giorni", "-30": "Scadute negli ultimi 30 giorni" };

function periodRange(p) {
  if (!p) return {};
  const n = Number(p), now = new Date();
  const other = new Date(now.getTime() + n * 86400000).toISOString();
  return n > 0 ? { date_to: other } : { date_from: other, date_to: now.toISOString() };
}

const Filters = ({ f, setF, commesse, users }) => {
  const cims = commesse.flatMap((c) => c.cimiteri || []);
  return (
    <div className="mb-6 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="dashboard-filters">
      <NativeSelect testid="filter-commessa" value={f.commessa_id} onChange={(v) => setF({ ...f, commessa_id: v, cimitero_id: "" })}>
        <option value="">Tutte le commesse</option>
        {commesse.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
      </NativeSelect>
      <NativeSelect testid="filter-cimitero" value={f.cimitero_id} onChange={(v) => setF({ ...f, cimitero_id: v })}>
        <option value="">Tutti i cimiteri</option>
        {cims.filter((c) => !f.commessa_id || c.commessa_id === f.commessa_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </NativeSelect>
      <NativeSelect testid="filter-responsabile" value={f.responsabile_id} onChange={(v) => setF({ ...f, responsabile_id: v })}>
        <option value="">Tutti i responsabili</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </NativeSelect>
      <NativeSelect testid="filter-priority" value={f.priority} onChange={(v) => setF({ ...f, priority: v })}>
        <option value="">Ogni priorità</option>
        {["alta", "normale"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
      </NativeSelect>
      <NativeSelect testid="filter-period" value={f.period} onChange={(v) => setF({ ...f, period: v })} className="col-span-2 sm:col-span-1">
        {Object.entries(PERIODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </NativeSelect>
    </div>
  );
};

const KpiTile = ({ k, onClick, solid }) => {
  const Icon = ICONS[k.key] || Layers;
  const solidCls = k.color === "red" ? "bg-[#f43b30] text-white" : "bg-[#ff9500] text-white";
  return (
    <button onClick={onClick} data-testid={`kpi-tile-${k.key}`}
      className={cn("tile-press flex flex-col items-start rounded-2xl p-4 text-left sm:p-5", solid && k.value > 0 ? solidCls : "card-soft")}>
      <div className="flex w-full items-center justify-between">
        <Icon className={cn("h-6 w-6", solid && k.value > 0 ? "text-white" : "text-[hsl(var(--brand))]")} />
        {!(solid && k.value > 0) && <Semaphore color={k.color} />}
      </div>
      <span className="mt-3 text-4xl font-extrabold leading-none" data-testid={`kpi-value-${k.key}`}>{k.value}</span>
      <span className={cn("mt-1.5 text-sm font-medium", solid && k.value > 0 ? "text-white/90" : "text-muted-foreground")}>{k.label}</span>
    </button>
  );
};

const Section = ({ title, children, action }) => (
  <section className="mb-8 min-w-0 fade-up">
    <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-bold">{title}</h2>{action}</div>
    {children}
  </section>
);

const DelegatedToMe = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/orders", { params: { delegated_to_me: true } }).then((r) => setRows(r.data)).catch(() => {});
  }, []);
  if (!rows.length) return null;
  return (
    <Section title="Affidati a te dal CEO: da assegnare">
      <div className="card-soft divide-y" data-testid="dashboard-delegated">
        {rows.map((o) => (
          <Link key={o.id} to={`/ordini/${o.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50" data-testid={`dashboard-delegated-${o.code}`}>
            <UserPlus className="h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{o.code} · {o.title}</p><p className="text-xs text-muted-foreground">{o.commessa_code} · {fmtWhen(o)}</p></div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </Section>
  );
};

const OrderRow = ({ o }) => (
  <Link to={`/ordini/${o.id}`} data-testid={`dashboard-order-${o.code}`} className={cn("flex items-center gap-3 border-b px-4 py-3 last:border-0 hover:bg-muted/50", o.is_overdue && "overdue-row")}>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold"><span className="font-mono text-xs text-muted-foreground">{o.code}</span> · {o.title}</p>
      <p className="truncate text-xs text-muted-foreground">{o.commessa_code} · {o.cimitero_name} · {o.assignee_names.join(", ") || "non assegnato"}</p>
    </div>
    <div className="hidden flex-col items-end gap-1 sm:flex"><StatusBadge status={o.status} /><OverdueBadge order={o} /></div>
    <ChevronRight className="h-4 w-4 text-zinc-400" />
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ commessa_id: "", cimitero_id: "", responsabile_id: "", priority: "", period: "" });
  const [data, setData] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [resps, setResps] = useState([]);

  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data));
    api.get("/users", { params: { role: "responsabile" } }).then((r) => setResps(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const { period, ...rest } = f;
    const params = Object.fromEntries(Object.entries({ ...rest, ...periodRange(period) }).filter(([, v]) => v));
    api.get("/dashboard/summary", { params }).then((r) => setData(r.data));
  }, [f]);

  const goKpi = (key) => {
    const { period, ...rest } = f;
    const params = new URLSearchParams(Object.entries({ kpi: key, ...rest, ...periodRange(period) }).filter(([, v]) => v));
    nav(`/ordini?${params}`);
  };

  if (!data) return <div className="text-muted-foreground" data-testid="dashboard-loading">Caricamento dashboard…</div>;
  const show = (k) => user.role !== "responsabile" || !RESP_HIDDEN.includes(k.key);
  const [first, second, ...others] = [data.kpis.find((k) => k.key === "overdue"), data.kpis.find((k) => k.key === "to_verify"), ...data.kpis.filter((k) => !["overdue", "to_verify"].includes(k.key) && show(k))];

  return (
    <div data-testid="ceo-dashboard">
      <Hero user={user} subtitle={`D.R. Multiservice srl · ${new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}`}
        right={<div className="hidden rounded-2xl bg-white/20 p-3 sm:block"><Building2 className="h-7 w-7" /></div>} />
      <Filters f={f} setF={setF} commesse={commesse} users={resps} />
      {user.role === "responsabile" && <DelegatedToMe />}

      <Section title="Situazione ordini">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile k={first} solid onClick={() => goKpi("overdue")} />
          <KpiTile k={{ ...second, color: second.value ? "yellow" : "green" }} solid onClick={() => goKpi("to_verify")} />
          {others.map((k) => <KpiTile key={k.key} k={k} onClick={() => goKpi(k.key)} />)}
        </div>
      </Section>

      <Section title="Criticità e ispezioni" action={<Link to="/mappe" className="text-sm font-semibold text-primary">Apri mappe</Link>}>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {data.crit_kpis.filter(show).map((k) => <KpiTile key={k.key} k={k} solid={k.key === "cr_urgent"} onClick={() => nav(k.link)} />)}
        </div>
      </Section>

      <Section title="Mezzi e attrezzature" action={<Link to="/mezzi" className="text-sm font-semibold text-primary">Apri inventario</Link>}>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {data.asset_kpis.filter(show).map((k) => <KpiTile key={k.key} k={k} solid={k.key === "assets_broken"} onClick={() => nav(k.link)} />)}
        </div>
      </Section>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Stato delle commesse">
          <div className="card-soft overflow-hidden">
            {data.per_commessa.map((c) => (
              <Link key={c.id} to={`/commesse/${c.id}`} data-testid={`dashboard-commessa-${c.code}`} className="flex items-center gap-4 border-b px-4 py-4 last:border-0 hover:bg-muted/50">
                <Semaphore color={c.color} />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-bold">{c.code} <DemoBadge show={c.is_demo} /></p>
                  <p className="truncate text-xs text-muted-foreground">{c.name} · Resp. {c.responsabile || "—"}</p>
                </div>
                <div className="flex shrink-0 gap-3 text-center text-xs">
                  <div><p className="text-lg font-extrabold">{c.open}</p>aperti</div>
                  <div className={c.overdue ? "text-red-600" : ""}><p className="text-lg font-extrabold">{c.overdue}</p>scaduti</div>
                  <div className="hidden sm:block"><p className="text-lg font-extrabold">{c.to_verify}</p>da verif.</div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
        <Section title="Responsabili">
          <div className="card-soft overflow-hidden">
            {data.per_responsabile.map((r) => (
              <button key={r.id} onClick={() => setF({ ...f, responsabile_id: r.id })} data-testid={`dashboard-resp-${r.id}`} className="flex w-full items-center gap-4 border-b px-4 py-3.5 text-left last:border-0 hover:bg-muted/50">
                <Semaphore color={r.color} />
                <span className="flex-1 font-semibold">{r.name}</span>
                <span className="text-right text-xs text-muted-foreground">{r.open} aperti · <b className={r.overdue ? "text-red-600" : ""}>{r.overdue} scaduti</b> · {r.to_verify} da verificare</span>
              </button>
            ))}
          </div>
        </Section>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Lavori scaduti" action={<button className="text-sm font-semibold text-primary" onClick={() => goKpi("overdue")}>Vedi tutti</button>}>
          <div className="card-soft overflow-hidden">{data.overdue_list.length ? data.overdue_list.map((o) => <OrderRow key={o.id} o={o} />) : <p className="p-6 text-sm text-muted-foreground">Nessun lavoro scaduto.</p>}</div>
        </Section>
        <Section title="Chiusure da approvare" action={<button className="text-sm font-semibold text-primary" onClick={() => goKpi("to_verify")}>Vedi tutte</button>}>
          <div className="card-soft overflow-hidden">{data.verify_list.length ? data.verify_list.map((o) => <OrderRow key={o.id} o={o} />) : <p className="p-6 text-sm text-muted-foreground">Nessuna chiusura in attesa.</p>}</div>
        </Section>
      </div>

      {["ceo", "amministrazione"].includes(user.role) && (
        <Section title="Segnalazioni di Toth" action={<Link to="/toth" className="text-sm font-semibold text-primary">Apri Toth</Link>}>
          <div className="card-soft overflow-hidden" data-testid="dashboard-toth-feed">
            {data.pending_proposals > 0 && <p className="border-b bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">{data.pending_proposals} proposte in attesa della tua decisione</p>}
            {data.toth_feed.length === 0 && <p className="p-6 text-sm text-muted-foreground">Nessuna azione recente.</p>}
            {data.toth_feed.map((a) => (
              <div key={a.id} className="flex items-start gap-3 border-b px-4 py-3 last:border-0">
                <div className="icon-chip h-9 w-9 shrink-0"><Sparkles className="h-4 w-4" /></div>
                <div className="min-w-0"><p className="text-sm font-semibold">{a.proposed_action}</p><p className="text-xs text-muted-foreground">{a.rule_name} · {fmtDT(a.at)}</p></div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {user.role === "ceo" && <Section title="Moduli in arrivo">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          {data.future_modules.map((m) => (
            <div key={m.key} data-testid={`future-module-${m.key}`} className="rounded-2xl border border-dashed bg-white/50 p-4 text-muted-foreground">
              <Lock className="h-4 w-4" /><p className="mt-2 text-sm font-semibold leading-snug">{m.label}</p><p className="mt-1 text-xs">Non disponibile · Fase {m.phase}</p>
            </div>
          ))}
        </div>
      </Section>}
    </div>
  );
}
